import { procedureLabel } from '@iculogbook/shared';
import type {
  LocalAnaestheticCase,
  LocalCase,
  LocalEdtEntry,
  LocalProcedure,
} from '@iculogbook/shared';
import { useLiveQuery } from 'dexie-react-hooks';

import { Button, Card } from '../../components/index.ts';
import { anaestheticRepository } from '../../db/repositories/anaesthetic.ts';
import { casesRepository } from '../../db/repositories/cases.ts';
import { edtRepository } from '../../db/repositories/edt.ts';
import { proceduresRepository } from '../../db/repositories/procedures.ts';

const DELETED_ON = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

type DeletedRow = {
  id: string;
  kind: 'case' | 'procedure' | 'edt' | 'anaesthetic';
  label: string;
  deletedAt: Date;
};

const KIND_LABELS: Record<DeletedRow['kind'], string> = {
  case: 'Case',
  procedure: 'Procedure',
  edt: 'EDT / Teaching',
  anaesthetic: 'Anaesthetic case',
};

function rowsFrom(
  cases: LocalCase[],
  procedures: LocalProcedure[],
  edtEntries: LocalEdtEntry[],
  anaestheticCases: LocalAnaestheticCase[],
): DeletedRow[] {
  const rows: DeletedRow[] = [
    ...cases.map((record) => ({
      id: record.id,
      kind: 'case' as const,
      label: record.title,
      deletedAt: record.deletedAt ?? record.updatedAt,
    })),
    ...procedures.map((record) => ({
      id: record.id,
      kind: 'procedure' as const,
      label: procedureLabel(record.procedureKey),
      deletedAt: record.deletedAt ?? record.updatedAt,
    })),
    ...edtEntries.map((record) => ({
      id: record.id,
      kind: 'edt' as const,
      label: record.title,
      deletedAt: record.deletedAt ?? record.updatedAt,
    })),
    ...anaestheticCases.map((record) => ({
      id: record.id,
      kind: 'anaesthetic' as const,
      label: record.title,
      deletedAt: record.deletedAt ?? record.updatedAt,
    })),
  ];

  return rows.sort((a, b) => b.deletedAt.getTime() - a.deletedAt.getTime());
}

/**
 * Settings → Recently deleted.
 *
 * Nothing here has actually been removed: a delete sets `deletedAt` and the row
 * stays, so restoring is just clearing that. This screen exists because the
 * project's worst failure is a trainee losing work, and an undo that expires in
 * five seconds is not much of a safety net at 4am.
 */
export function RecentlyDeleted({ userId }: { userId: string | null }) {
  const rows = useLiveQuery(async () => {
    if (userId === null) return [];

    const [cases, procedures, edtEntries, anaestheticCases] = await Promise.all([
      casesRepository.listDeleted(userId),
      proceduresRepository.listDeleted(userId),
      edtRepository.listDeleted(userId),
      anaestheticRepository.listDeleted(userId),
    ]);

    return rowsFrom(cases, procedures, edtEntries, anaestheticCases);
  }, [userId]);

  async function restore(row: DeletedRow): Promise<void> {
    if (userId === null) return;

    if (row.kind === 'case') await casesRepository.restore(userId, row.id);
    else if (row.kind === 'edt') await edtRepository.restore(userId, row.id);
    else if (row.kind === 'anaesthetic') await anaestheticRepository.restore(userId, row.id);
    else await proceduresRepository.restore(userId, row.id);
  }

  return (
    <Card title="Recently deleted">
      {rows === undefined || rows.length === 0 ? (
        <p className="text-sm text-muted">Nothing deleted. Entries you delete appear here.</p>
      ) : (
        <>
          <ul className="flex flex-col gap-1">
            {rows.map((row) => (
              <li
                key={row.id}
                className="flex min-h-touch items-center justify-between gap-3 border-b border-line py-1 last:border-b-0"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm text-text">{row.label}</span>
                  <span className="text-xs text-faint">
                    {KIND_LABELS[row.kind]} · deleted {DELETED_ON.format(row.deletedAt)}
                  </span>
                </span>

                <Button variant="ghost" onClick={() => void restore(row)}>
                  Restore
                </Button>
              </li>
            ))}
          </ul>

          <p className="pt-3 text-xs text-faint">
            Deleted entries are kept for at least 30 days. Restoring a case does not bring back the
            procedures inside it unless you restore those too.
          </p>
        </>
      )}
    </Card>
  );
}
